import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { stringify } from 'qs'
import axiosRetry from 'axios-retry'

import { toast } from '@/hooks/useToast'
import { buildSignInUrl, currentReturnPath } from '@/lib/authReturnPath'
import { clearSessionStorage } from '@/lib/sessionCleanup'
import {
  clearReauthAttempts,
  isReauthCircuitOpen,
  recordReauthAttempt,
} from '@/lib/reauthCircuitBreaker'
import { isNoAuthRoute, isPublicRoute } from '@/middleware'
import { ResponseCode } from '@/types/response.type'

/** 인터셉터가 요청에 붙이는 내부 플래그. */
type AuthAwareConfig = AxiosRequestConfig & {
  /** 원 요청을 이미 한 번 재시도했다. */
  _retry?: boolean
  /** 이 요청은 401이어도 인증 처리(재시도/리다이렉트)를 타지 않는다. */
  _skipAuthHandling?: boolean
}

// Sign-out 진행 중에는 인터셉터가 401 리다이렉트로 끼어들지 않는다 — 어차피 로그인
// 페이지로 떠나는 중이기 때문이다. 반드시 **짧은 구간**이어야 한다.
//
// 예전에는 플래그를 켜는 코드만 있고 끄는 코드가 없었다. sign-out 요청이 실패하면
// `signOutSession` 이 catch로 빠져 마지막 줄의 `window.location.href` 에 도달하지 못했고,
// full page load가 일어나지 않아 플래그가 페이지 수명 내내 켜진 채 남았다. 그 상태에서는
// 모든 401이 아래 early-return에 걸려 재시도도 리다이렉트도 없이 조용히 reject되어,
// 사용자가 아무 안내 없이 죽은 화면에 갇혔다.
//
// 그래서 플래그를 직접 노출하지 않고 스코프 헬퍼만 내보낸다 — 복구 누락이 구조적으로 불가능하다.
let signOutInFlight = false

export const withSignOutInFlight = async <T>(run: () => Promise<T>): Promise<T> => {
  signOutInFlight = true
  try {
    return await run()
  } finally {
    signOutInFlight = false
  }
}

const API = axios.create({
  baseURL: process.env.BACKEND_API_URL,
  timeout: 300000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
  },
  withCredentials: true,
  paramsSerializer: (params) => {
    return stringify(params, { arrayFormat: 'brackets' })
  },
})

API.interceptors.request.use(
  function (config: any) {
    config.headers = {
      ...config.headers,
      'X-Forwarded-Host': typeof window !== 'undefined' && window.location.hostname,
    }
    config.headers = config.headers ?? {}
    config.withCredentials = true

    return config
  },
  function (error: any) {
    console.log(error)
    return Promise.reject(error)
  }
)

// 401 응답 중 재시도 대상 에러 코드
// - UNAUTHORIZED (-1): 일반 auth 실패
// - MISSING_TOKEN (-17): 토큰 cookie 누락 (일시적 race condition 가능)
// - REFRESH_TOKEN (-101): refresh token 무효
const AUTH_ERROR_CODES: number[] = [
  ResponseCode.UNAUTHORIZED,
  ResponseCode.MISSING_TOKEN,
  ResponseCode.REFRESH_TOKEN,
]

const isAuthError = (error: AxiosError): boolean =>
  error.response?.status === 401 &&
  AUTH_ERROR_CODES.includes((error.response?.data as { code?: number } | undefined)?.code as number)

// 세션이 정말 죽었는지는 미들웨어(`middleware.ts` `validateUserToken`)와 **같은 근거**로
// 판정해야 한다. 인터셉터가 "임의 XHR이 401이면 죽은 것"이라 단정하고 미들웨어는
// `/users/me` 로 판정하면, 쿠키가 서버에서는 유효한데 브라우저 XHR만 401인 상황에서
// 두 판단이 엇갈린다. 그러면 인터셉터는 sign_in으로 보내고, 미들웨어는 "로그인한
// 사용자는 sign_in에 들어올 수 없다"며 앱으로 되돌려보내는 무한 리다이렉트 루프가 된다
// (staging 실측: 30초에 네비게이션 20~30회, sign_in 도달 실패).
// 확인은 짧게 끝나야 한다. 세션이 살아 있는 경우 이 대기가 곧 사용자가 느끼는 지연이다.
const SESSION_CHECK_TIMEOUT_MS = 4000

/** 세션 확인 결과. `unknown` 은 "확인 실패" — 살아있음으로 취급하지 않는다. */
type SessionState = 'dead' | 'alive' | 'unknown'

let sessionCheck: Promise<SessionState> | null = null

const checkSession = (): Promise<SessionState> => {
  if (!sessionCheck) {
    const config: AuthAwareConfig = {
      timeout: SESSION_CHECK_TIMEOUT_MS,
      // 이 확인 요청이 401일 때 다시 인증 처리를 타면 무한 재귀가 된다
      _skipAuthHandling: true,
    }
    sessionCheck = API.get(`${alphaPath}/users/me`, config)
      .then((): SessionState => 'alive')
      .catch((e: unknown): SessionState => {
        if (!axios.isAxiosError(e)) return 'unknown'
        if (e.response?.status === 401) return 'dead'
        // timeout / 5xx / 네트워크 — 판정 불가. 살아있다고 단정하면 사용자가 안내도
        // 없이 갇히고, 죽었다고 단정하면 멀쩡한 세션을 끊는다.
        return 'unknown'
      })
      .finally(() => {
        sessionCheck = null
      })
  }
  // 동시에 401난 요청이 몇 개든 확인 요청은 1회만 나간다
  return sessionCheck
}

// 이 페이지 인스턴스에서 재인증 처리를 더 진행하지 않는다.
// (리다이렉트를 이미 시작했거나, circuit breaker가 열렸거나, 의도적 sign-out 중)
let reauthHandlingDone = false

/**
 * 사용자가 스스로 로그아웃해 페이지를 떠나는 중임을 알린다.
 *
 * `window.location.href` 대입은 네비게이션을 **예약**할 뿐 JS를 멈추지 않는다. 그 사이
 * 남아있던 폴링 요청이 401을 받으면, 방금 로그아웃했으니 세션 확인은 당연히 'dead'가
 * 나오고 `?fallback=session_expired` 로 다시 이동한다 — 직접 로그아웃한 사용자에게
 * "세션이 만료되었습니다" 에러 토스트가 뜨고, 다음 로그인 때 로그아웃했던 페이지로
 * 되돌아간다. 이동이 커밋되기 전 이 구간을 닫는다.
 */
export const markSignOutNavigation = () => {
  reauthHandlingDone = true
}

// 동시에 401난 요청 N개가 각자 판정·기록하면 안 된다. 그러면 circuit breaker의
// 10초/3회 예산이 "루프 1회"가 아니라 "동시 요청 수"로 소진되어, 루프가 한 번도 없었는데
// 첫 페이지에서 곧바로 열려버린다. 판정과 후속 조치 전체를 하나의 promise로 묶는다.
let reauthDecision: Promise<void> | null = null

const redirectToSignInIfSessionDead = (): Promise<void> => {
  if (reauthHandlingDone) return Promise.resolve()
  if (!reauthDecision) {
    reauthDecision = decideReauth().finally(() => {
      reauthDecision = null
    })
  }
  return reauthDecision
}

const decideReauth = async (): Promise<void> => {
  const state = await checkSession()

  // sign-out 등으로 판정 중에 상황이 바뀌었을 수 있다
  if (reauthHandlingDone) return

  if (state === 'alive') {
    // 이 401은 세션 문제가 아니므로 리다이렉트하지 않는다 — 이 분기가 미들웨어와의
    // 핑퐁을 원천 차단한다. 세션이 확실히 살아있을 때만 시도 기록을 비운다.
    clearReauthAttempts()
    return
  }

  // 'dead' 든 'unknown' 이든 이 판정 1회당 시도 1회만 기록한다.
  if (isReauthCircuitOpen(recordReauthAttempt())) {
    reauthHandlingDone = true
    notifyReauthStuck()
    return
  }

  // 판정 불가(timeout·5xx·네트워크)면 리다이렉트하지 않는다. 살아있다고 단정하면
  // 사용자가 갇히고, 죽었다고 단정하면 멀쩡한 세션을 끊는다. 반복되면 위 breaker가 알린다.
  if (state === 'unknown') return

  reauthHandlingDone = true
  endDeadSession()
}

// The session is gone: clear what sign-out would have cleared (connector tokens
// and the rest — see sessionCleanup) and go to sign-in. Clearing must never
// keep the user on a dead page, so the redirect runs even if storage throws.
export const endDeadSession = (): void => {
  try {
    clearSessionStorage()
  } catch {
    // fall through to the redirect
  }
  // replace: 히스토리를 오염시키지 않아 뒤로가기로 죽은 페이지에 되돌아가지 않는다
  window.location.replace(buildSignInUrl(currentReturnPath()))
}

// 조용히 멈추면 안 된다 — 안내 없이 갇히는 것이야말로 이 수정이 없애려는 증상이다.
// sticky: toastStore는 새 토스트가 뜨면 기존 토스트를 2초로 단축하는데, 이 안내가 뜨는
// 상황은 responseWrapper의 일반 에러 토스트도 함께 쏟아지는 상황이라 그냥 두면 잘린다.
const notifyReauthStuck = () => {
  toast({
    type: 'error',
    text: 'We could not restore your session. Please reload the page.',
    duration: 60000,
    sticky: true,
  })
}

API.interceptors.response.use(
  (response) => {
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AuthAwareConfig | undefined

    if (
      typeof window === 'undefined' ||
      !originalRequest ||
      originalRequest._retry ||
      originalRequest._skipAuthHandling ||
      signOutInFlight ||
      !isAuthError(error) ||
      isPublicRoute(window.location.pathname) ||
      isNoAuthRoute(window.location.pathname)
    ) {
      return Promise.reject(error)
    }

    // 1차 실패 → 백엔드가 handle_invalid_token으로 refresh했을 가능성 있음
    // (동시 요청 race condition 등으로 첫 요청이 구 쿠키로 나갔을 수 있음)
    // 원 요청을 한 번 재시도 — 새 쿠키로 성공하면 유저 작업이 중단 없이 이어짐
    originalRequest._retry = true
    try {
      return await API(originalRequest)
    } catch (retryError) {
      // 재시도도 401이면 refresh로 살릴 수 없다. 다만 "이 요청이 401"인 것과
      // "세션이 죽었다"는 다르다 — 후자를 확인한 뒤에만 로그인 페이지로 보낸다.
      if (axios.isAxiosError(retryError) && isAuthError(retryError)) {
        await redirectToSignInIfSessionDead()
      }
      return Promise.reject(retryError)
    }
  }
)

axiosRetry(API, {
  retries: 3,
  retryDelay: (retryCount) => {
    return retryCount * 300
  },
  retryCondition: (error) => {
    switch (error.code) {
      case AxiosError.ERR_NETWORK:
        return true
      default:
        return false
    }
  },
})

export const responseWrapper = async <T>(
  block: () => Promise<AxiosResponse<T>>,
  isAlert = true
) => {
  try {
    const res = await block()
    return res.data
  } catch (e) {
    const { response } = e as AxiosError
    if (response && !response.status.toString().startsWith('40') && response.data) {
      const data = response?.data as T
      if (isAlert) {
        if (process.env.NODE_ENV === 'development') {
          toast({ type: 'error', text: `[${response?.status}] ${data}`, duration: 3000 })
        } else {
          toast({
            type: 'error',
            text: 'An error has occurred. Please try again later.',
            duration: 3000,
          })
        }
      }
    }
    throw e
  }
}

export const authPath = '/api/auth'

export const alphaPath = process.env.NODE_ENV == 'development' ? '' : '/api/alpha'
export const superTokenPath = '/u/api'
export default API
